import React from 'react';
import {
  Plus,
  Trash,
  X,
  CaretDown,
} from '@phosphor-icons/react';
import { Dialog, Portal, CloseButton } from '@chakra-ui/react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import {
  BLOCK_TYPE,
  BLOCK_TYPE_COLORS,
  BLOCK_TYPE_LABELS,
  LOAD_UNIT,
  LOAD_UNIT_LABELS,
  EXERCISE_METHOD,
  EXERCISE_METHOD_LABELS,
  getLoadUnitsForInputMode,
  isEnduranceInputMode,
} from '../../../enums/programming';
import {
  FormExercise,
  FormBlock,
  FormDay,
  SetDetail,
  EmomRoundDetail,
  emptyLoad,
} from './workoutBuilderTypes';
import MethodFieldsAdapter, { MethodParams } from './MethodFieldsAdapter';
import ConditioningStepsEditor from './ConditioningSteps/ConditioningStepsEditor';
import EmomSubExerciseRow from './EmomSubExerciseRow';
import EmomRoundTable from './EmomRoundTable';
import DraggableExerciseRow from './DraggableExerciseRow';
import DroppableSupersetGroup from './DroppableSupersetGroup';
import BlockLibraryPickerModal from './BlockLibraryPickerModal';
import PerSetGrid, { VARIABLE_LOAD_METHODS } from './PerSetGrid';
import styles from './WorkoutBuilderModal.module.scss';
import { IBlock } from '../../../models/programming';

interface BlockExerciseListProps {
  currentDay: FormDay;
  dayLocalId: string;
  blocks: FormBlock[];
  addBlock: (dayLocalId: string) => void;
  addBlockFromTemplate: (dayLocalId: string, block: IBlock) => void;
  removeBlock: (dayLocalId: string, blockLocalId: string) => void;
  updateBlock: (dayLocalId: string, blockLocalId: string, patch: Partial<FormBlock>) => void;
  addExercise: (dayLocalId: string, blockLocalId: string) => void;
  removeExercise: (dayLocalId: string, blockLocalId: string, exLocalId: string) => void;
  updateExercise: (dayLocalId: string, blockLocalId: string, exLocalId: string, patch: Partial<FormExercise>) => void;
  createSuperset: (dayLocalId: string, block: FormBlock, exLocalIds: string[]) => void;
  removeFromSuperset: (dayLocalId: string, blockLocalId: string, exLocalId: string) => void;
  updateSupersetSets: (dayLocalId: string, blockLocalId: string, group: string, sets: string) => void;
  toggleExerciseSelection: (blockLocalId: string, exLocalId: string) => void;
  toggleExpanded: (exLocalId: string) => void;
  setExpandedExercises: React.Dispatch<React.SetStateAction<Set<string>>>;
  handleDragStart: (event: DragStartEvent, block: FormBlock) => void;
  handleDragOver: (event: DragOverEvent) => void;
  handleDragEnd: (event: DragEndEvent, dayLocalId: string, block: FormBlock) => void;
  selectedExercises: Record<string, Set<string>>;
  expandedExercises: Set<string>;
  activeExercise: { ex: FormExercise; blockLocalId: string } | null;
  overExerciseId: string | null;
  exercisePickerTarget: { dayLocalId: string; blockLocalId: string; exLocalId: string } | null;
  setExercisePickerTarget: (target: { dayLocalId: string; blockLocalId: string; exLocalId: string } | null) => void;
  newBlockType: Record<string, BLOCK_TYPE>;
  setNewBlockType: React.Dispatch<React.SetStateAction<Record<string, BLOCK_TYPE>>>;
  isNonStandardMethod: (method: EXERCISE_METHOD | '') => boolean;
  /** Atleta di anteprima per la conversione %→assoluto degli step (BUG E). */
  previewAthleteId?: string;
}

/** Calcola i round EMOM normalizzando durata e intervallo in secondi */
function calcEmomRoundsFromConfig(config: Record<string, string>): number {
  const durationUnit = (config.durationUnit || 'min') as 'min' | 'sec';
  const intervalUnit = (config.intervalUnit || 'sec') as 'min' | 'sec';
  const durationVal = parseFloat(config.totalDurationMin ?? '') || 0;
  const intervalVal = parseFloat(config.intervalSec ?? '') || 0;
  if (durationVal <= 0 || intervalVal <= 0) return 0;
  const durationInSec = durationUnit === 'min' ? durationVal * 60 : durationVal;
  const intervalInSec = intervalUnit === 'min' ? intervalVal * 60 : intervalVal;
  if (intervalInSec <= 0 || intervalInSec > durationInSec) return 0;
  return Math.floor(durationInSec / intervalInSec);
}

/** Costruisce il testo di riepilogo compatto per un esercizio EMOM */
function buildEmomSummary(ex: FormExercise): string {
  const durationUnit = (ex.methodConfig.durationUnit || 'min') as 'min' | 'sec';
  const intervalUnit = (ex.methodConfig.intervalUnit || 'sec') as 'min' | 'sec';
  const durationVal = parseFloat(ex.methodConfig.totalDurationMin ?? '') || 0;
  const intervalVal = parseFloat(ex.methodConfig.intervalSec ?? '') || 0;
  const durationInSec = durationUnit === 'min' ? durationVal * 60 : durationVal;
  const intervalInSec = intervalUnit === 'min' ? intervalVal * 60 : intervalVal;
  const rounds = intervalInSec > 0 && durationInSec > 0
    ? Math.floor(durationInSec / intervalInSec)
    : 0;

  const parts: string[] = [];
  if (durationVal) parts.push(`${durationVal}${durationUnit === 'min' ? 'min' : 's'}`);
  if (intervalVal) parts.push(`ogni ${intervalVal}${intervalUnit === 'sec' ? 's' : 'min'}`);
  if (rounds) parts.push(`${rounds} round`);

  const reps = ex.methodConfig.emomReps || ex.reps;
  const loadVal = ex.loads[0]?.value;
  const loadUnitLabel = ex.loads[0]?.unit ? (LOAD_UNIT_LABELS[ex.loads[0].unit as LOAD_UNIT] || ex.loads[0].unit) : 'kg';
  if (reps) parts.push(loadVal ? `${reps} reps @ ${loadVal} ${loadUnitLabel}` : `${reps} reps`);
  else if (loadVal) parts.push(`@ ${loadVal} ${loadUnitLabel}`);

  return parts.join(' · ');
}

/**
 * Crea un array di SetDetail iniziale per i metodi a serie variabile.
 * Pre-popola reps e carico dal valore globale dell'esercizio.
 */
function buildVariableSetsDetail(
  numSets: number,
  globalReps: string,
  globalRest: string,
  globalLoadValue: string,
  globalLoadUnit: LOAD_UNIT,
): SetDetail[] {
  return Array.from({ length: numSets }, (_, i) => ({
    setNumber: i + 1,
    reps: globalReps,
    rest: globalRest,
    loadValue: globalLoadValue,
    loadUnit: globalLoadUnit,
  }));
}

const BlockExerciseList: React.FC<BlockExerciseListProps> = ({
  currentDay,
  dayLocalId,
  blocks,
  addBlock,
  addBlockFromTemplate,
  removeBlock,
  updateBlock,
  addExercise,
  removeExercise,
  updateExercise,
  createSuperset,
  removeFromSuperset,
  updateSupersetSets,
  toggleExerciseSelection,
  toggleExpanded,
  setExpandedExercises,
  handleDragStart,
  handleDragOver,
  handleDragEnd,
  selectedExercises,
  expandedExercises,
  activeExercise,
  overExerciseId,
  setExercisePickerTarget,
  newBlockType,
  setNewBlockType,
  isNonStandardMethod,
  previewAthleteId,
}) => {
  /** Stato per il block library picker */
  const [blockPickerDayLocalId, setBlockPickerDayLocalId] = React.useState<string | null>(null);

  /** Stato per la conferma trim righe Per Round con valori custom */
  const [pendingRoundTrim, setPendingRoundTrim] = React.useState<{
    dayLocalId: string;
    blockLocalId: string;
    exLocalId: string;
    exPatch: Partial<FormExercise>;
  } | null>(null);

  /**
   * Lazy-init legacy pyramid/dropset: se setsDetail è vuoto per un metodo
   * a carico variabile, lo popola una volta sola tramite useEffect (evita
   * side-effect durante il render che causano micro-loop).
   */
  React.useEffect(() => {
    blocks.forEach((block) => {
      block.exercises.forEach((ex) => {
        if (VARIABLE_LOAD_METHODS.has(ex.method) && ex.setsDetail.length === 0) {
          const initialDetail = buildVariableSetsDetail(
            parseInt(ex.sets, 10) || 1,
            ex.reps,
            ex.rest,
            ex.loads[0]?.value ?? '',
            (ex.loads[0]?.unit ?? LOAD_UNIT.KG) as LOAD_UNIT,
          );
          if (initialDetail.length > 0) {
            updateExercise(dayLocalId, block._localId, ex._localId, { setsDetail: initialDetail });
          }
        }
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks]);

  return (
    <div className={styles.blocksList}>
      {blocks.map((block) => (
        <div
          key={block._localId}
          className={styles.blockCard}
          style={{
            borderLeftColor: BLOCK_TYPE_COLORS[block.type],
          }}
        >
          {/* Block header */}
          <div className={styles.blockHeader}>
            <div className={styles.blockHeaderLeft}>
              <div
                className={styles.blockColorDot}
                style={{
                  background: BLOCK_TYPE_COLORS[block.type],
                }}
              />
              <input
                type="text"
                className={styles.blockTitleInput}
                value={block.title}
                onChange={(e) =>
                  updateBlock(
                    dayLocalId,
                    block._localId,
                    { title: e.target.value },
                  )
                }
                placeholder="Titolo blocco"
              />
              <select
                className={styles.blockTypeSelect}
                value={block.type}
                onChange={(e) => {
                  const newType = e.target.value as BLOCK_TYPE;
                  // Sincronizza title con label nuovo solo se l'utente
                  // non l'ha già personalizzato (= title coincide col
                  // label del type corrente).
                  const titleIsDefault =
                    block.title.trim() === BLOCK_TYPE_LABELS[block.type];
                  updateBlock(dayLocalId, block._localId, {
                    type: newType,
                    ...(titleIsDefault
                      ? { title: BLOCK_TYPE_LABELS[newType] }
                      : {}),
                  });
                }}
              >
                {Object.values(BLOCK_TYPE).map((t) => (
                  <option key={t} value={t}>
                    {BLOCK_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {(() => {
                const standaloneIds = block.exercises
                  .filter(e => !e.supersetGroup)
                  .map(e => e._localId);
                if (standaloneIds.length === 0) return null;
                const allExpanded = standaloneIds.every(id => expandedExercises.has(id));
                return (
                  <button
                    type="button"
                    className={styles.expandAllBtn}
                    onClick={() => {
                      setExpandedExercises(prev => {
                        const next = new Set(prev);
                        if (allExpanded) {
                          standaloneIds.forEach(id => next.delete(id));
                        } else {
                          standaloneIds.forEach(id => next.add(id));
                        }
                        return next;
                      });
                    }}
                  >
                    {allExpanded ? 'Comprimi tutti' : 'Espandi tutti'}
                  </button>
                );
              })()}
              <button
                type="button"
                className={styles.removeBlockBtn}
                onClick={() =>
                  removeBlock(
                    dayLocalId,
                    block._localId,
                  )
                }
                title="Rimuovi blocco"
              >
                <Trash size={15} />
              </button>
            </div>
          </div>

          {/* Exercises table */}
          <div className={styles.exercisesTable}>
            <DndContext
              collisionDetection={closestCenter}
              onDragStart={(event) => handleDragStart(event, block)}
              onDragOver={handleDragOver}
              onDragEnd={(event) => handleDragEnd(event, dayLocalId, block)}
            >
            {/* Block-level header with superset action */}
            {(() => {
              const blockSelected = selectedExercises[block._localId];
              const selectedCount = blockSelected ? blockSelected.size : 0;
              return selectedCount >= 2 ? (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
                  <button
                    type="button"
                    className={styles.supersetCreateBtn}
                    onClick={() =>
                      createSuperset(
                        dayLocalId,
                        block,
                        Array.from(blockSelected),
                      )
                    }
                  >
                    Raggruppa in Superset
                  </button>
                </div>
              ) : null;
            })()}

            <div className={styles.exercisesTableHeader}>
              <span style={{ width: '32px', flexShrink: 0 }} />
              <span className={styles.colExercise}>
                Esercizio
              </span>
              <span className={styles.colSets}>Serie</span>
              <span className={styles.colReps}>Reps</span>
              <span className={styles.colLoad}>Carico</span>
              <span className={styles.colUnit}>Unità</span>
              <span className={styles.colRest}>Recupero</span>
              <span className={styles.colNotes}>Note</span>
              <span className={styles.colActions} />
            </div>

            {(() => {
              // Build ordered render list: group superset exercises together
              const rendered: React.ReactNode[] = [];
              const processedGroups = new Set<string>();

              block.exercises.forEach((ex) => {
                if (ex.supersetGroup && !processedGroups.has(ex.supersetGroup)) {
                  // Render entire superset group
                  processedGroups.add(ex.supersetGroup);
                  const groupExercises = block.exercises.filter(
                    (e) => e.supersetGroup === ex.supersetGroup,
                  );
                  const sharedSets = groupExercises[0]?.sets ?? '3';

                  rendered.push(
                    <DroppableSupersetGroup
                      key={`superset-${block._localId}-${ex.supersetGroup}`}
                      groupId={`${block._localId}-${ex.supersetGroup}`}
                      supersetGroup={ex.supersetGroup}
                      isOver={overExerciseId === `drop-superset-${block._localId}-${ex.supersetGroup}`}
                    >
                      {/* Superset header bar */}
                      <div className={styles.supersetHeaderBar}>
                        <span className={styles.supersetLabel}>
                          Superset {ex.supersetGroup}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span className={styles.supersetFieldLabel}>Serie:</span>
                          <input
                            type="number"
                            className={styles.smallInput}
                            value={sharedSets}
                            min={1}
                            style={{ width: '48px' }}
                            onChange={(e) =>
                              updateSupersetSets(
                                dayLocalId,
                                block._localId,
                                ex.supersetGroup,
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span className={styles.supersetFieldLabel}>Recupero:</span>
                          <input
                            type="text"
                            className={styles.smallInput}
                            value={groupExercises[0]?.rest ?? ''}
                            placeholder='90"'
                            style={{ width: '56px' }}
                            onChange={(e) => {
                              // Propaga il recupero a tutti gli esercizi del superset
                              groupExercises.forEach((ge) => {
                                updateExercise(
                                  dayLocalId,
                                  block._localId,
                                  ge._localId,
                                  { rest: e.target.value },
                                );
                              });
                            }}
                          />
                        </div>
                      </div>

                      {/* Superset exercises */}
                      {groupExercises.map((gex, gIdx) => {
                        const label = `${gex.supersetGroup}${gIdx + 1}`;
                        return (
                          <div
                            key={gex._localId}
                            className={styles.exerciseRow}
                            style={{ paddingLeft: '8px', background: 'transparent' }}
                          >
                            {/* ROW 1: exercise name + method + laterality + actions */}
                            <div className={styles.exerciseRowTop}>
                              {/* Spacer (no checkbox inside superset) */}
                              <div style={{ width: '18px', flexShrink: 0 }} />

                              {/* colExercise: label + clickable name label */}
                              <div className={styles.colExercise}>
                                <span className={styles.supersetExerciseLabel}>
                                  {label}
                                </span>
                                <div
                                  style={{
                                    flex: 1,
                                    padding: '5px 8px',
                                    borderRadius: 6,
                                    border: '1px solid #e7e7e7',
                                    fontSize: 13,
                                    fontFamily: "'Satoshi', sans-serif",
                                    color: gex.exerciseName ? '#00212c' : '#b5b5b5',
                                    cursor: 'pointer',
                                    background: 'var(--c-card-bg)',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    transition: 'border-color 0.15s',
                                  }}
                                  onClick={() => setExercisePickerTarget({
                                    dayLocalId: currentDay._localId,
                                    blockLocalId: block._localId,
                                    exLocalId: gex._localId,
                                  })}
                                  title={gex.exerciseName || 'Clicca per cercare un esercizio'}
                                >
                                  {gex.exerciseName || 'Cerca esercizio...'}
                                </div>
                              </div>

                              {/* Method selector */}
                              <div className={styles.selectWrapper} style={{ flexShrink: 0 }}>
                                <select
                                  className={styles.methodSelect}
                                  value={gex.method}
                                  onChange={(e) =>
                                    updateExercise(
                                      dayLocalId,
                                      block._localId,
                                      gex._localId,
                                      { method: e.target.value as EXERCISE_METHOD },
                                    )
                                  }
                                >
                                  <option value="">Metodo</option>
                                  {Object.values(EXERCISE_METHOD).map((m) => (
                                    <option key={m} value={m}>{m}</option>
                                  ))}
                                </select>
                                <CaretDown size={12} className={styles.selectIcon} />
                              </div>

                              {/* Per lato toggle */}
                              <button
                                type="button"
                                title="Esercizio unilaterale (DX / SX)"
                                onClick={() =>
                                  updateExercise(
                                    dayLocalId,
                                    block._localId,
                                    gex._localId,
                                    { isUnilateral: !gex.isUnilateral },
                                  )
                                }
                                style={{
                                  flexShrink: 0,
                                  padding: '2px 7px',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  borderRadius: '10px',
                                  border: gex.isUnilateral ? 'none' : '1px solid #e7e7e7',
                                  background: gex.isUnilateral ? '#063444' : 'transparent',
                                  color: gex.isUnilateral ? '#fff' : '#b5b5b5',
                                  cursor: 'pointer',
                                  letterSpacing: '0.2px',
                                  lineHeight: '1.6',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {gex.isUnilateral ? 'DX/SX' : 'Per lato'}
                              </button>

                              {/* Actions */}
                              <div className={styles.colActions} style={{ marginLeft: 'auto', display: 'flex', gap: '2px' }}>
                                <button
                                  type="button"
                                  className={`${styles.removeExBtn} ${styles.supersetRemoveBtn}`}
                                  title="Rimuovi dal superset"
                                  onClick={() =>
                                    removeFromSuperset(dayLocalId, block._localId, gex._localId)
                                  }
                                >
                                  ↩
                                </button>
                                <button
                                  type="button"
                                  className={styles.removeExBtn}
                                  onClick={() => removeExercise(dayLocalId, block._localId, gex._localId)}
                                  title="Rimuovi esercizio"
                                >
                                  <X size={13} weight="bold" />
                                </button>
                              </div>
                            </div>

                            {/* ROW 2: params */}
                            <div className={styles.exerciseRowParams}>
                              {/* Reps */}
                              <div className={styles.colReps}>
                                <span className={styles.colLabel}>Reps</span>
                                <input
                                  type="text"
                                  className={styles.smallInput}
                                  placeholder="8"
                                  value={gex.reps}
                                  onChange={(e) =>
                                    updateExercise(dayLocalId, block._localId, gex._localId, { reps: e.target.value })
                                  }
                                />
                              </div>

                              {/* Load value */}
                              <div className={styles.colLoad}>
                                <span className={styles.colLabel}>Carico</span>
                                <input
                                  type="text"
                                  className={styles.smallInput}
                                  placeholder="—"
                                  value={gex.loads[0]?.value ?? ''}
                                  onChange={(e) => {
                                    const newLoads = [...gex.loads];
                                    if (newLoads.length === 0) newLoads.push(emptyLoad());
                                    newLoads[0] = { ...newLoads[0], value: e.target.value };
                                    updateExercise(dayLocalId, block._localId, gex._localId, { loads: newLoads });
                                  }}
                                />
                              </div>

                              {/* Load unit */}
                              <div className={styles.colUnit}>
                                <span className={styles.colLabel}>Unità</span>
                                <div className={styles.selectWrapper}>
                                  <select
                                    className={styles.unitSelect}
                                    value={gex.loads[0]?.unit ?? LOAD_UNIT.KG}
                                    onChange={(e) => {
                                      const newLoads = [...gex.loads];
                                      if (newLoads.length === 0) newLoads.push(emptyLoad());
                                      newLoads[0] = { ...newLoads[0], unit: e.target.value as LOAD_UNIT };
                                      updateExercise(dayLocalId, block._localId, gex._localId, { loads: newLoads });
                                    }}
                                  >
                                    {getLoadUnitsForInputMode(gex.defaultInputMode).map((u) => (
                                      <option key={u} value={u}>{LOAD_UNIT_LABELS[u]}</option>
                                    ))}
                                  </select>
                                  <CaretDown size={12} className={styles.selectIcon} />
                                </div>
                              </div>

                              {/* Rest */}
                              <div className={styles.colRest}>
                                <span className={styles.colLabel}>Recupero</span>
                                <input
                                  type="text"
                                  className={styles.smallInput}
                                  placeholder='90"'
                                  value={gex.rest}
                                  onChange={(e) =>
                                    updateExercise(dayLocalId, block._localId, gex._localId, { rest: e.target.value })
                                  }
                                />
                              </div>

                              {/* Notes */}
                              <div className={styles.colNotes}>
                                <span className={styles.colLabel}>Note</span>
                                <input
                                  type="text"
                                  className={styles.smallInput}
                                  placeholder="—"
                                  value={gex.notes}
                                  onChange={(e) =>
                                    updateExercise(dayLocalId, block._localId, gex._localId, { notes: e.target.value })
                                  }
                                />
                              </div>

                              {/* Per set toggle — superset */}
                              <div className={styles.colPerSet}>
                                <span className={styles.colLabel}>&nbsp;</span>
                                <button
                                  type="button"
                                  className={styles.perSetToggle}
                                  title={gex.setsDetail.length > 0 ? 'Disattiva personalizzazione set' : 'Personalizza reps/recupero per set'}
                                  onClick={() => {
                                    if (gex.setsDetail.length > 0) {
                                      updateExercise(dayLocalId, block._localId, gex._localId, { setsDetail: [] });
                                    } else {
                                      const numSets = parseInt(sharedSets, 10) || 1;
                                      const details: SetDetail[] = Array.from({ length: numSets }, (_, i) => ({
                                        setNumber: i + 1,
                                        reps: gex.reps,
                                        rest: gex.rest,
                                      }));
                                      updateExercise(dayLocalId, block._localId, gex._localId, { setsDetail: details });
                                    }
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: gex.setsDetail.length > 0 ? '#063444' : '#b5b5b5',
                                    padding: '2px 0',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {gex.setsDetail.length > 0 ? '✓ Per set' : '⚙ Per set'}
                                </button>
                              </div>
                            </div>

                            {/* Per-set detail grid — superset */}
                            {gex.setsDetail.length > 0 && (
                              <PerSetGrid
                                setsDetail={gex.setsDetail}
                                showLoad={VARIABLE_LOAD_METHODS.has(gex.method)}
                                globalLoadUnit={(gex.loads[0]?.unit ?? LOAD_UNIT.KG) as LOAD_UNIT}
                                inputMode={gex.defaultInputMode}
                                onChange={(updated) =>
                                  updateExercise(dayLocalId, block._localId, gex._localId, { setsDetail: updated })
                                }
                              />
                            )}
                          </div>
                        );
                      })}
                    </DroppableSupersetGroup>,
                  );
                } else if (!ex.supersetGroup) {
                  // Render standalone exercise
                  const isExpanded = expandedExercises.has(ex._localId);
                  rendered.push(
                    <React.Fragment key={ex._localId}>
                      <DraggableExerciseRow
                        exLocalId={ex._localId}
                        blockLocalId={block._localId}
                        isOver={overExerciseId === ex._localId}
                      >
                        {(dragHandle) => (
                      <>
                      {!isExpanded ? (
                        /* COLLAPSED VIEW */
                        <div
                          className={styles.exerciseRow}
                          style={{ padding: '4px 10px', cursor: 'pointer' }}
                          onClick={() => toggleExpanded(ex._localId)}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                            {dragHandle}
                            <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: ex.exerciseName ? '#00212c' : '#b5b5b5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {ex.exerciseName || 'Esercizio non selezionato'}
                            </span>
                            <span style={{ fontSize: 11, color: 'var(--c-text-3)', whiteSpace: 'nowrap' }}>
                              {isEnduranceInputMode(ex.defaultInputMode)
                                ? `${ex.steps.length} step${ex.steps.length === 1 ? '' : 's'}`
                                : ex.method === EXERCISE_METHOD.EMOM
                                ? buildEmomSummary(ex)
                                : `${ex.sets}×${ex.reps || '—'}${ex.loads[0]?.value ? ` @ ${ex.loads[0].value} ${LOAD_UNIT_LABELS[ex.loads[0].unit as LOAD_UNIT] || ex.loads[0].unit || 'kg'}` : ''}${ex.rest ? ` · ${ex.rest}` : ''}`
                              }
                            </span>
                            {ex.method && ex.method !== EXERCISE_METHOD.STANDARD && (
                              <span className={styles.methodBadgeCollapsed}>
                                {EXERCISE_METHOD_LABELS[ex.method as EXERCISE_METHOD] || ex.method}
                              </span>
                            )}
                            {ex.isUnilateral && (
                              <span className={styles.unilateralBadge}>DX/SX</span>
                            )}
                            <CaretDown size={14} style={{ color: 'var(--c-text-3)', flexShrink: 0 }} />
                            <button
                              type="button"
                              className={styles.removeExBtn}
                              onClick={(e) => { e.stopPropagation(); removeExercise(dayLocalId, block._localId, ex._localId); }}
                              title="Rimuovi"
                            >
                              <Trash size={13} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* EXPANDED VIEW */
                      <div className={styles.exerciseRow}>
                        {/* ROW 1: drag handle + checkbox + exercise name + method + laterality + actions */}
                        <div className={styles.exerciseRowTop}>
                          {/* Drag handle + checkbox for superset selection */}
                          <div style={{ width: '32px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '2px' }}>
                            {dragHandle}
                            <input
                              type="checkbox"
                              style={{ cursor: 'pointer', width: '13px', height: '13px' }}
                              checked={selectedExercises[block._localId]?.has(ex._localId) ?? false}
                              onChange={() => toggleExerciseSelection(block._localId, ex._localId)}
                            />
                          </div>

                          {/* colExercise: clickable name label */}
                          <div className={styles.colExercise}>
                            <div
                              style={{
                                flex: 1,
                                padding: '5px 8px',
                                borderRadius: 6,
                                border: '1px solid #e7e7e7',
                                fontSize: 13,
                                fontFamily: "'Satoshi', sans-serif",
                                color: ex.exerciseName ? '#00212c' : '#b5b5b5',
                                cursor: 'pointer',
                                background: 'var(--c-card-bg)',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                transition: 'border-color 0.15s',
                              }}
                              onClick={() => setExercisePickerTarget({
                                dayLocalId: currentDay._localId,
                                blockLocalId: block._localId,
                                exLocalId: ex._localId,
                              })}
                              title={ex.exerciseName || 'Clicca per cercare un esercizio'}
                            >
                              {ex.exerciseName || 'Cerca esercizio...'}
                            </div>
                          </div>

                          {/* Method selector — nascosto per i mezzi endurance:
                              uno step-based non ha "metodo" (BUG A/D) */}
                          {!isEnduranceInputMode(ex.defaultInputMode) && (
                          <div className={styles.selectWrapper} style={{ flexShrink: 0 }}>
                            <select
                              className={styles.methodSelect}
                              value={ex.method}
                              onChange={(e) => {
                                const newMethod = e.target.value as EXERCISE_METHOD;
                                const patch: Partial<FormExercise> = { method: newMethod };
                                // Attiva automaticamente la griglia per-serie per metodi a serie variabile
                                if (VARIABLE_LOAD_METHODS.has(newMethod)) {
                                  const numSets = parseInt(ex.sets, 10) || 1;
                                  patch.setsDetail = buildVariableSetsDetail(
                                    numSets,
                                    ex.reps,
                                    ex.rest,
                                    ex.loads[0]?.value ?? '',
                                    (ex.loads[0]?.unit ?? LOAD_UNIT.KG) as LOAD_UNIT,
                                  );
                                } else if (VARIABLE_LOAD_METHODS.has(ex.method)) {
                                  // Uscendo da un metodo variabile: azzera setsDetail
                                  patch.setsDetail = [];
                                }
                                updateExercise(dayLocalId, block._localId, ex._localId, patch);
                              }}
                            >
                              <option value="">Metodo</option>
                              {Object.values(EXERCISE_METHOD).map(
                                (m) => (
                                  <option key={m} value={m}>
                                    {m}
                                  </option>
                                ),
                              )}
                            </select>
                            <CaretDown
                              size={12}
                              className={styles.selectIcon}
                            />
                          </div>
                          )}

                          {/* Per lato toggle */}
                          <button
                            type="button"
                            title="Esercizio unilaterale (DX / SX)"
                            onClick={() =>
                              updateExercise(
                                dayLocalId,
                                block._localId,
                                ex._localId,
                                { isUnilateral: !ex.isUnilateral },
                              )
                            }
                            style={{
                              flexShrink: 0,
                              padding: '2px 7px',
                              fontSize: '10px',
                              fontWeight: 600,
                              borderRadius: '10px',
                              border: ex.isUnilateral ? 'none' : '1px solid #e7e7e7',
                              background: ex.isUnilateral ? '#063444' : 'transparent',
                              color: ex.isUnilateral ? '#fff' : '#b5b5b5',
                              cursor: 'pointer',
                              letterSpacing: '0.2px',
                              lineHeight: '1.6',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {ex.isUnilateral ? 'DX/SX' : 'Per lato'}
                          </button>

                          {/* Remove exercise button */}
                          <div className={styles.colActions} style={{ marginLeft: 'auto' }}>
                            <button
                              type="button"
                              className={styles.removeExBtn}
                              onClick={() =>
                                removeExercise(
                                  dayLocalId,
                                  block._localId,
                                  ex._localId,
                                )
                              }
                              title="Rimuovi esercizio"
                            >
                              <Trash size={13} />
                            </button>
                          </div>
                        </div>

                        {/* ROW 2: params */}
                        <div className={styles.exerciseRowParams}>
                          {/* Mezzo endurance (corsa/bici/macchina/sprint) → costruttore di step
                              automatico (BUG A); altrimenti standard/MethodFieldsAdapter */}
                          {isEnduranceInputMode(ex.defaultInputMode) ? (
                            <ConditioningStepsEditor
                              steps={ex.steps}
                              previewAthleteId={previewAthleteId}
                              exerciseSlug={ex.slug}
                              onChange={(steps) =>
                                updateExercise(dayLocalId, block._localId, ex._localId, { steps })
                              }
                            />
                          ) : isNonStandardMethod(ex.method) ? (
                            <MethodFieldsAdapter
                              method={ex.method as EXERCISE_METHOD}
                              inputMode={ex.defaultInputMode}
                              params={{
                                sets: ex.sets,
                                reps: ex.reps,
                                loadValue: ex.loads[0]?.value ?? '',
                                loadUnit: (ex.loads[0]?.unit ?? LOAD_UNIT.KG) as LOAD_UNIT,
                                rest: ex.rest,
                                ...ex.methodConfig,
                              }}
                              onChange={(patch: Partial<MethodParams>) => {
                                const exPatch: Partial<FormExercise> = {};
                                const configPatch: Record<string, string> = { ...ex.methodConfig };

                                if (patch.sets !== undefined) {
                                  exPatch.sets = patch.sets;
                                  // Per metodi a serie variabile: ridimensiona la griglia per-serie
                                  if (VARIABLE_LOAD_METHODS.has(ex.method) && ex.setsDetail.length > 0) {
                                    const numSets = parseInt(patch.sets, 10) || 1;
                                    const current = ex.setsDetail;
                                    const globalLoadUnit = (ex.loads[0]?.unit ?? LOAD_UNIT.KG) as LOAD_UNIT;
                                    exPatch.setsDetail = Array.from({ length: numSets }, (_, i) => {
                                      if (i < current.length) return current[i];
                                      const last = current[current.length - 1];
                                      return {
                                        setNumber: i + 1,
                                        reps: last?.reps ?? ex.reps,
                                        rest: last?.rest ?? ex.rest,
                                        loadValue: last?.loadValue ?? ex.loads[0]?.value ?? '',
                                        loadUnit: last?.loadUnit ?? globalLoadUnit,
                                      };
                                    });
                                  }
                                }
                                if (patch.reps !== undefined) exPatch.reps = patch.reps;
                                if (patch.rest !== undefined) exPatch.rest = patch.rest;
                                if (patch.loadValue !== undefined || patch.loadUnit !== undefined) {
                                  const newLoads = [...ex.loads];
                                  if (newLoads.length === 0) newLoads.push(emptyLoad());
                                  if (patch.loadValue !== undefined) newLoads[0] = { ...newLoads[0], value: patch.loadValue };
                                  if (patch.loadUnit !== undefined) newLoads[0] = { ...newLoads[0], unit: patch.loadUnit };
                                  exPatch.loads = newLoads;
                                }

                                const standardKeys = new Set(['sets', 'reps', 'rest', 'loadValue', 'loadUnit']);
                                for (const [key, value] of Object.entries(patch)) {
                                  if (!standardKeys.has(key) && value !== undefined) {
                                    configPatch[key] = String(value);
                                  }
                                }
                                exPatch.methodConfig = configPatch;

                                // EMOM: sync emomRoundDetails quando i round cambiano
                                if (ex.method === EXERCISE_METHOD.EMOM && ex.emomRoundDetails.length > 0) {
                                  const newConfig = { ...ex.methodConfig, ...configPatch };
                                  const newRounds = calcEmomRoundsFromConfig(newConfig);
                                  if (newRounds > 0 && newRounds !== ex.emomRoundDetails.length) {
                                    if (newRounds > ex.emomRoundDetails.length) {
                                      // Aggiungi righe copiando dall'ultima
                                      const lastRow = ex.emomRoundDetails[ex.emomRoundDetails.length - 1];
                                      const toAdd = newRounds - ex.emomRoundDetails.length;
                                      const newDetails = [...ex.emomRoundDetails];
                                      for (let i = 0; i < toAdd; i++) {
                                        newDetails.push({ ...lastRow, round: ex.emomRoundDetails.length + i + 1 });
                                      }
                                      exPatch.emomRoundDetails = newDetails;
                                    } else {
                                      // Trim: verifica se le righe da rimuovere hanno valori custom
                                      const toTrim = ex.emomRoundDetails.slice(newRounds);
                                      const hasCustom = toTrim.some(
                                        (rd) => rd.reps || rd.loadValue || rd.tempo || rd.notes,
                                      );
                                      if (hasCustom) {
                                        setPendingRoundTrim({
                                          dayLocalId,
                                          blockLocalId: block._localId,
                                          exLocalId: ex._localId,
                                          exPatch: { ...exPatch, emomRoundDetails: ex.emomRoundDetails.slice(0, newRounds) },
                                        });
                                        return;
                                      }
                                      exPatch.emomRoundDetails = ex.emomRoundDetails.slice(0, newRounds);
                                    }
                                  }
                                }

                                updateExercise(
                                  dayLocalId,
                                  block._localId,
                                  ex._localId,
                                  exPatch,
                                );
                              }}
                            />
                          ) : (
                            <>
                              {/* Sets */}
                              <div className={styles.colSets}>
                                <span className={styles.colLabel}>Serie</span>
                                <input
                                  type="number"
                                  className={styles.smallInput}
                                  value={ex.sets}
                                  min={1}
                                  onChange={(e) => {
                                    const newSets = e.target.value;
                                    const patch: Partial<FormExercise> = { sets: newSets };
                                    if (ex.setsDetail.length > 0) {
                                      const numSets = parseInt(newSets, 10) || 1;
                                      const currentDetails = ex.setsDetail;
                                      const lastDetail = currentDetails[currentDetails.length - 1];
                                      const newDetails: SetDetail[] = Array.from({ length: numSets }, (_, i) => {
                                        if (i < currentDetails.length) return currentDetails[i];
                                        // Eredita loadValue/loadUnit dall'ultimo set esistente
                                        return {
                                          setNumber: i + 1,
                                          reps: lastDetail?.reps ?? ex.reps,
                                          rest: lastDetail?.rest ?? ex.rest,
                                          loadValue: lastDetail?.loadValue ?? ex.loads[0]?.value ?? '',
                                          loadUnit: lastDetail?.loadUnit ?? (ex.loads[0]?.unit as LOAD_UNIT) ?? LOAD_UNIT.KG,
                                        };
                                      });
                                      patch.setsDetail = newDetails;
                                    }
                                    updateExercise(dayLocalId, block._localId, ex._localId, patch);
                                  }}
                                />
                              </div>

                              {/* Reps */}
                              <div className={styles.colReps}>
                                <span className={styles.colLabel}>Reps</span>
                                <input
                                  type="text"
                                  className={styles.smallInput}
                                  placeholder="8"
                                  value={ex.reps}
                                  onChange={(e) =>
                                    updateExercise(
                                      dayLocalId,
                                      block._localId,
                                      ex._localId,
                                      { reps: e.target.value },
                                    )
                                  }
                                />
                              </div>

                              {/* Load value */}
                              <div className={styles.colLoad}>
                                <span className={styles.colLabel}>Carico</span>
                                <input
                                  type="text"
                                  className={styles.smallInput}
                                  placeholder="—"
                                  value={ex.loads[0]?.value ?? ''}
                                  onChange={(e) => {
                                    const newLoads = [...ex.loads];
                                    if (newLoads.length === 0)
                                      newLoads.push(emptyLoad());
                                    newLoads[0] = {
                                      ...newLoads[0],
                                      value: e.target.value,
                                    };
                                    updateExercise(
                                      dayLocalId,
                                      block._localId,
                                      ex._localId,
                                      { loads: newLoads },
                                    );
                                  }}
                                />
                              </div>

                              {/* Load unit */}
                              <div className={styles.colUnit}>
                                <span className={styles.colLabel}>Unità</span>
                                <div className={styles.selectWrapper}>
                                  <select
                                    className={styles.unitSelect}
                                    value={
                                      ex.loads[0]?.unit ?? LOAD_UNIT.KG
                                    }
                                    onChange={(e) => {
                                      const newLoads = [...ex.loads];
                                      if (newLoads.length === 0)
                                        newLoads.push(emptyLoad());
                                      newLoads[0] = {
                                        ...newLoads[0],
                                        unit: e.target.value as LOAD_UNIT,
                                      };
                                      updateExercise(
                                        dayLocalId,
                                        block._localId,
                                        ex._localId,
                                        { loads: newLoads },
                                      );
                                    }}
                                  >
                                    {getLoadUnitsForInputMode(ex.defaultInputMode).map((u) => (
                                      <option key={u} value={u}>
                                        {LOAD_UNIT_LABELS[u]}
                                      </option>
                                    ))}
                                  </select>
                                  <CaretDown
                                    size={12}
                                    className={styles.selectIcon}
                                  />
                                </div>
                              </div>

                              {/* Rest */}
                              <div className={styles.colRest}>
                                <span className={styles.colLabel}>Recupero</span>
                                <input
                                  type="text"
                                  className={styles.smallInput}
                                  placeholder='90"'
                                  value={ex.rest}
                                  onChange={(e) =>
                                    updateExercise(
                                      dayLocalId,
                                      block._localId,
                                      ex._localId,
                                      { rest: e.target.value },
                                    )
                                  }
                                />
                              </div>

                              {/* Per-set customization toggle */}
                              <div className={styles.colPerSet}>
                                <span className={styles.colLabel}>&nbsp;</span>
                                {VARIABLE_LOAD_METHODS.has(ex.method) ? (
                                  /* Metodi a serie variabile: badge fisso, non disattivabile */
                                  <span
                                    className={styles.perSetToggle}
                                    title="Reps e carico per ogni serie — obbligatorio per questo metodo"
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      fontSize: 10,
                                      fontWeight: 600,
                                      color: '#063444',
                                      padding: '2px 0',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    ✓ Per serie
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    className={styles.perSetToggle}
                                    title={ex.setsDetail.length > 0 ? 'Disattiva personalizzazione set' : 'Personalizza reps/recupero per set'}
                                    onClick={() => {
                                      if (ex.setsDetail.length > 0) {
                                        updateExercise(dayLocalId, block._localId, ex._localId, { setsDetail: [] });
                                      } else {
                                        const numSets = parseInt(ex.sets, 10) || 1;
                                        const details: SetDetail[] = Array.from({ length: numSets }, (_, i) => ({
                                          setNumber: i + 1,
                                          reps: ex.reps,
                                          rest: ex.rest,
                                        }));
                                        updateExercise(dayLocalId, block._localId, ex._localId, { setsDetail: details });
                                      }
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      fontSize: 10,
                                      fontWeight: 600,
                                      color: ex.setsDetail.length > 0 ? '#063444' : '#b5b5b5',
                                      padding: '2px 0',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {ex.setsDetail.length > 0 ? '✓ Per set' : '⚙ Per set'}
                                  </button>
                                )}
                              </div>
                            </>
                          )}

                          {/* Notes — always visible */}
                          <div className={styles.colNotes}>
                            <span className={styles.colLabel}>Note</span>
                            <input
                              type="text"
                              className={styles.smallInput}
                              placeholder="—"
                              value={ex.notes}
                              onChange={(e) =>
                                updateExercise(
                                  dayLocalId,
                                  block._localId,
                                  ex._localId,
                                  { notes: e.target.value },
                                )
                              }
                            />
                          </div>
                        </div>

                        {/* Per-set detail grid */}
                        {(() => {
                          const isVariableMethod = VARIABLE_LOAD_METHODS.has(ex.method);
                          // effectiveSetsDetail: usa setsDetail già inizializzato dall'useEffect
                          // (la lazy-init legacy viene gestita sopra, non qui)
                          const effectiveSetsDetail = ex.setsDetail;

                          if (effectiveSetsDetail.length === 0) return null;

                          return (
                            <PerSetGrid
                              setsDetail={effectiveSetsDetail}
                              showLoad={isVariableMethod}
                              globalLoadUnit={(ex.loads[0]?.unit ?? LOAD_UNIT.KG) as LOAD_UNIT}
                              inputMode={ex.defaultInputMode}
                              onChange={(updated) =>
                                updateExercise(dayLocalId, block._localId, ex._localId, { setsDetail: updated })
                              }
                            />
                          );
                        })()}
                      </div>
                      )}
                      </>
                        )}
                      </DraggableExerciseRow>

                      {/* EMOM section — sub-exercises OR Per Round table */}
                      {isExpanded && ex.method === EXERCISE_METHOD.EMOM && (
                        <div className={styles.emomSection}>
                          <div className={styles.emomSectionHeader}>
                            <span className={styles.emomSectionTitle}>
                              Esercizi del round
                            </span>
                            {/* Toggle "Per Round" — visibile solo se non ci sono sub-esercizi */}
                            {ex.emomSubExercises.length === 0 && (
                              <button
                                type="button"
                                className={styles.emomPerRoundToggle}
                                style={{
                                  color: ex.emomRoundDetails.length > 0 ? '#063444' : '#b5b5b5',
                                }}
                                title={ex.emomRoundDetails.length > 0 ? 'Disattiva personalizzazione per round' : 'Personalizza reps/carico per ogni round'}
                                onClick={() => {
                                  if (ex.emomRoundDetails.length > 0) {
                                    updateExercise(dayLocalId, block._localId, ex._localId, { emomRoundDetails: [] });
                                  } else {
                                    const rounds = calcEmomRoundsFromConfig(ex.methodConfig);
                                    if (rounds <= 0) return;
                                    const globalReps = ex.methodConfig.emomReps || ex.reps || '';
                                    const globalLoadValue = ex.loads[0]?.value ?? '';
                                    const globalLoadUnit = (ex.loads[0]?.unit ?? LOAD_UNIT.KG) as LOAD_UNIT;
                                    const globalTempo = ex.methodConfig.emomTempo || '';
                                    const details: EmomRoundDetail[] = Array.from({ length: rounds }, (_, i) => ({
                                      round: i + 1,
                                      reps: globalReps,
                                      loadValue: globalLoadValue,
                                      loadUnit: globalLoadUnit,
                                      tempo: globalTempo,
                                      notes: '',
                                    }));
                                    updateExercise(dayLocalId, block._localId, ex._localId, { emomRoundDetails: details });
                                  }
                                }}
                              >
                                {ex.emomRoundDetails.length > 0 ? '✓ Per Round' : '⚙ Per Round'}
                              </button>
                            )}
                          </div>

                          {/* Sub-esercizi del round (modalità multi-esercizio) */}
                          {ex.emomSubExercises.map((sub, subIdx) => (
                            <EmomSubExerciseRow
                              key={subIdx}
                              sub={sub}
                              onChangeExercise={(exerciseId, exerciseName) => {
                                const updated = [...ex.emomSubExercises];
                                updated[subIdx] = { ...updated[subIdx], exerciseId, exerciseName };
                                updateExercise(dayLocalId, block._localId, ex._localId, { emomSubExercises: updated });
                              }}
                              onChangeReps={(reps) => {
                                const updated = [...ex.emomSubExercises];
                                updated[subIdx] = { ...updated[subIdx], reps };
                                updateExercise(dayLocalId, block._localId, ex._localId, { emomSubExercises: updated });
                              }}
                              onRemove={() => {
                                const updated = ex.emomSubExercises.filter((_, i) => i !== subIdx);
                                updateExercise(dayLocalId, block._localId, ex._localId, { emomSubExercises: updated });
                              }}
                            />
                          ))}

                          {/* Pulsante aggiungi esercizio — solo se non è attiva la tabella Per Round */}
                          {ex.emomRoundDetails.length === 0 && (
                            <button
                              type="button"
                              className={`${styles.emomAddExerciseBtn}${ex.emomSubExercises.length > 0 ? ` ${styles.emomAddExerciseBtnSpaced}` : ''}`}
                              onClick={() => {
                                const updated = [...ex.emomSubExercises, { exerciseId: '', exerciseName: '', reps: '' }];
                                updateExercise(dayLocalId, block._localId, ex._localId, { emomSubExercises: updated });
                              }}
                            >
                              <Plus size={11} weight="bold" />
                              Aggiungi esercizio
                            </button>
                          )}

                          {/* Tabella Per Round */}
                          {ex.emomRoundDetails.length > 0 && (
                            <EmomRoundTable
                              rounds={ex.emomRoundDetails}
                              inputMode={ex.defaultInputMode}
                              onChange={(updated) =>
                                updateExercise(dayLocalId, block._localId, ex._localId, { emomRoundDetails: updated })
                              }
                            />
                          )}
                        </div>
                      )}
                    </React.Fragment>,
                  );
                }
              });

              return rendered;
            })()}

            <DragOverlay>
              {activeExercise && activeExercise.blockLocalId === block._localId ? (
                <div className={styles.dragOverlayCard}>
                  <span className={styles.dragOverlayName}>
                    {activeExercise.ex.exerciseName || 'Esercizio'}
                  </span>
                  <span className={styles.dragOverlayHint}>
                    {overExerciseId ? '↓ Rilascia per creare Superset' : '⠿ Trascina su un altro esercizio'}
                  </span>
                </div>
              ) : null}
            </DragOverlay>
            </DndContext>

            {/* Add exercise */}
            <button
              type="button"
              className={styles.addExBtn}
              onClick={() =>
                addExercise(
                  dayLocalId,
                  block._localId,
                )
              }
            >
              <Plus size={13} weight="bold" />
              Esercizio
            </button>
          </div>
        </div>
      ))}

      {/* Conferma trim righe Per Round con valori custom */}
      <Dialog.Root
        open={pendingRoundTrim !== null}
        onOpenChange={(e) => { if (!e.open) setPendingRoundTrim(null); }}
        placement="center"
        size="sm"
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Riduzione round</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                Alcuni round da rimuovere contengono personalizzazioni. Procedendo verranno eliminati definitivamente. Continuare?
              </Dialog.Body>
              <Dialog.Footer style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className={styles.expandAllBtn}
                  onClick={() => setPendingRoundTrim(null)}
                >
                  Annulla
                </button>
                <button
                  type="button"
                  className={styles.supersetCreateBtn}
                  onClick={() => {
                    if (!pendingRoundTrim) return;
                    updateExercise(
                      pendingRoundTrim.dayLocalId,
                      pendingRoundTrim.blockLocalId,
                      pendingRoundTrim.exLocalId,
                      pendingRoundTrim.exPatch,
                    );
                    setPendingRoundTrim(null);
                  }}
                >
                  Conferma
                </button>
              </Dialog.Footer>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      {/* Add block row */}
      <div className={styles.addBlockRow}>
        <div className={styles.selectWrapper}>
          <select
            className={styles.addBlockTypeSelect}
            value={
              newBlockType[currentDay._localId] ||
              BLOCK_TYPE.MAX_STRENGTH
            }
            onChange={(e) =>
              setNewBlockType((prev) => ({
                ...prev,
                [currentDay._localId]: e.target
                  .value as BLOCK_TYPE,
              }))
            }
          >
            {Object.values(BLOCK_TYPE).map((t) => (
              <option key={t} value={t}>
                {BLOCK_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
          <CaretDown size={12} className={styles.selectIcon} />
        </div>
        <button
          type="button"
          className={styles.addBlockBtn}
          onClick={() => addBlock(currentDay._localId)}
        >
          <Plus size={14} weight="bold" />
          Nuovo blocco
        </button>
        <button
          type="button"
          className={styles.addBlockBtn}
          style={{ background: 'transparent', color: '#063444', border: '1.5px solid #063444' }}
          onClick={() => setBlockPickerDayLocalId(currentDay._localId)}
        >
          Dalla libreria
        </button>
      </div>

      {/* Block library picker modal */}
      {blockPickerDayLocalId && (
        <BlockLibraryPickerModal
          isOpen={!!blockPickerDayLocalId}
          dayLocalId={blockPickerDayLocalId}
          onSelect={(dlId, block) => addBlockFromTemplate(dlId, block)}
          onClose={() => setBlockPickerDayLocalId(null)}
        />
      )}
    </div>
  );
};

export default BlockExerciseList;
